import React, { useEffect } from "react";
import _ from "lodash";
import cx from "classnames";
import { useQueryClient } from "@tanstack/react-query";
import { EXPERIENCES, STORYBOARDS, FETCH } from "src/query/queryKeys";

import Slider from "src/components/slider";
import Wizard from "src/components/wizard";
import { Input } from "src/components/input";
import Button from "src/components/button";
import Reveal from "src/components/animation";
import { StoryboardType } from "src/types/experiences/storyboardSettings/storyboard";
import { Template } from "src/types/experiences/templates";
import {
  BLANK_TEMPLATE_IDS,
  STORYBOARD_CREATED,
  getAllowedContentTypes,
} from "src/pages/experiences/constants/storyboard";
import { useWorkspaceSubscription } from "src/pages/experiences/hooks/useWorkspaceSubscription";
import {
  buildCleverstoryUrl,
  buildCreationTargetPath,
} from "src/pages/experiences/utils/cleverstoryRedirect";
import { ContentTypeSelector } from "./ContentTypeSelector";
import { TemplatePanel } from "./TemplatePanel";

import styles from "./createStoryboardDialog.module.scss";

const DEFAULT_STORYBOARD_NAME = "";

interface CreateStoryboardDialogProps {
  show: boolean;
  onClose: () => void;
  workspaceId: string;
  listId?: string | null;
}

export const CreateStoryboardDialog: React.FC<CreateStoryboardDialogProps> = ({
  show,
  onClose,
  workspaceId,
  listId = null,
}) => {
  const [currentStep, setCurrentStep] = React.useState(0);
  const [name, setName] = React.useState(DEFAULT_STORYBOARD_NAME);
  const [contentType, setContentType] = React.useState<StoryboardType | null>(
    null,
  );
  const [error, setError] = React.useState<string | null>(null);
  const queryClient = useQueryClient();
  const subscription = useWorkspaceSubscription();

  const allowedTypes = React.useMemo(
    () =>
      getAllowedContentTypes(
        subscription,
        Object.keys(BLANK_TEMPLATE_IDS) as StoryboardType[],
      ),
    [subscription],
  );

  const showTemplatePanel = currentStep === 1 && contentType !== null;

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const cleverstoryOrigin = process.env.REACT_APP_CLEVERSTORY_APP_URL;
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== cleverstoryOrigin) return;
      if (event.data?.type === STORYBOARD_CREATED) {
        queryClient.invalidateQueries({ queryKey: [EXPERIENCES, STORYBOARDS, FETCH] });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [queryClient]);

  React.useEffect(() => {
    if (!show) {
      setCurrentStep(0);
      setName(DEFAULT_STORYBOARD_NAME);
      setContentType(null);
      setError(null);
    }
  }, [show]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setError(null);
  };

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
  };

  const openCreation = (templateId: string) => {
    const targetPath = buildCreationTargetPath(
      workspaceId,
      templateId,
      name,
      listId,
    );
    window.open(buildCleverstoryUrl(targetPath), "_blank")?.focus();
    onClose();
  };

  const handleCreateBlank = () => {
    if (!contentType) return;
    const blankId = BLANK_TEMPLATE_IDS[contentType];
    if (!blankId) return;
    openCreation(blankId);
  };

  const handleCreateFromTemplate = (template: Template) => {
    openCreation(template.id);
  };

  const handleNext = () => {
    if (currentStep === 0) {
      if (_.isEmpty(_.trim(name))) {
        setError("Please provide a valid name");
        return;
      }
      setCurrentStep(1);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleNext();
  };

  const handleClose = () => {
    onClose();
  };

  const isFirstStep = currentStep === 0;
  const blankDisabled = !contentType || !BLANK_TEMPLATE_IDS[contentType];

  return (
    <Slider.Dialog
      className={styles.createStoryboardDialog}
      width={showTemplatePanel ? "1035px" : "380px"}
      show={show}
      onClose={handleClose}
    >
      <div className={styles.dialogLayout}>
        <div className={styles.leftPanel}>
          <div className={styles.leftHeader}>
            <h2 className={styles.leftTitle}>
              creating <br />
              new storyboard
            </h2>
          </div>

          <div className={styles.leftBody}>
            <Wizard.Pane
              className={styles.wizard}
              currentStep={currentStep}
              gap={0}
              onStepChange={handleStepChange}
            >
              <Wizard.Step name="Naming" completed={!_.isEmpty(_.trim(name))}>
                <form
                  className={cx(styles.step, styles.nameStep)}
                  onSubmit={handleSubmit}
                >
                  <Input
                    autoFocus
                    className={styles.nameInput}
                    placeholder="Add a board name..."
                    value={name}
                    onChange={handleNameChange}
                  />
                  <Reveal show={!!error}>
                    <div className={styles.error}>
                      <i className="fa-solid fa-triangle-exclamation" />
                      {error}
                    </div>
                  </Reveal>
                  <p className={styles.stepDescription}>
                    Start creating your storyboard here!
                    
                    Choose a professionally designed template from the library or build something entirely from scratch, then tailor every detail to bring your ideas to life in a compelling and interactive experience.
                  </p>
                </form>
              </Wizard.Step>

              <Wizard.Step name="Content type" completed={true}>
                <div className={styles.step}>
                  <ContentTypeSelector
                    selected={contentType}
                    onSelect={setContentType}
                    allowedTypes={allowedTypes}
                  />
                </div>
              </Wizard.Step>
            </Wizard.Pane>
          </div>

          <div className={styles.bottomFade} />

          <div className={styles.leftActions}>
            <Button
              icon={isFirstStep ? "paperflite-right-arrow" : "paperflite-tick"}
              text={isFirstStep ? "Next" : "Create"}
              variant="primary"
              disabled={!isFirstStep && blankDisabled}
              onClick={isFirstStep ? handleNext : handleCreateBlank}
            />
            <Button text="Cancel" variant="secondary" onClick={handleClose} />
          </div>
        </div>

        {showTemplatePanel && (
          <TemplatePanel
            contentType={contentType}
            onEdit={handleCreateFromTemplate}
          />
        )}
      </div>
    </Slider.Dialog>
  );
};
